import "../style.scss";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Autocomplete, Chip, Tab } from "@mui/material";
import * as prismic from "@prismicio/client";
import { useAllPrismicDocumentsByType } from "@prismicio/react";
import ChangelogModal from "components/ChangelogModal";
import InfoTooltip from "components/shared/InfoTooltip";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import { StyledTabs } from "components/shared/StyledTabs";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useFormatChannels } from "containers/Channels/utils";
import { PortalType } from "contexts/portal.context";
import { currenciesList } from "currenciesList";
import { BodyHighlight, SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { formatLocaleDateString } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { Fragment, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { bulkInviteToPortal } from "store/adminPanelSlice";
import { fetchChannels } from "store/channelStore";
import { deleteIntegration, IntegrationStatus } from "store/integrationsStore";
import { fetchExportUnpaidAccounting, fetchPeriods } from "store/periodStore";
import { patchCompanyInfo, putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import type { IDistributor, TCurrencyData } from "../types";
import { hardcodedDistributorList, hardcodedDistributorObject } from "./CreateCompany";
import { AddPeriodType } from "./HiddenSettings/AddPeriodType";
import { BooleanSettingRow, filterTextContent } from "./HiddenSettings/BooleanSettingRow";
import { BOOLEAN_SETTINGS } from "./HiddenSettings/booleanSettingsConfig";
import { ExcludeZeroRoyaltiesStatementsOnMerged } from "./HiddenSettings/ExcludeZeroRoyaltiesStatementsOnMerged";
import { HiddenStockLinesOnStatement } from "./HiddenSettings/HiddenStockLinesOnStatement";
import { SETTINGS_CATEGORIES, SETTINGS_CATEGORY, type SettingsCategoryId } from "./HiddenSettings/settingsCategories";

type TAccountingSoftware = {
    id: string;
    name?: string;
};

const allAccountingSoftwares: TAccountingSoftware[] = [
    { id: "sage" },
    { id: "cogilog" },
    { id: "cegid" },
    { id: "cegid_gect", name: "Cegid On-prem (GECT)" },
    { id: "lunii", name: "MaCompta.fr (Lunii)" },
    { id: "arte", name: "SAGE FRP 1000 (ARTE)" },
    { id: "pennylane", name: "Pennylane" },
];

const CompanyHiddenSettings = () => {
    const portalType = useContext(PortalType);
    const dispatch = useAppDispatch();
    const { channels } = useAppSelector((state) => state.channels);
    const { periods } = useAppSelector((state) => state.period);
    const { companySettings } = useAppSelector((state) => state.settings);
    const integrations = useAppSelector((state) => state.integrations.integrations);
    const { getCompanySettings, companyCurrency } = useCompanySettings();
    const filteredCurrencies = currenciesList().filter((currency) => currency.code !== companyCurrency);

    const usesPortal = getCompanySettings("uses_portal");
    const usesMinimumGuaranteed = getCompanySettings("uses_minimum_guaranteed_payment");
    const availableDistributors = JSON.parse(getCompanySettings("available_distributors") || "[]");
    const currentAccountingSoftware = getCompanySettings("accounting_software");
    const authorizedPublisherSeats = getCompanySettings("authorized_publisher_seats");
    const authorizedAgreementTemplatesLimit = getCompanySettings("authorized_agreement_templates_limit");
    const activeCurrencies = JSON.parse(getCompanySettings("active_alternative_currencies") || "[]");

    const [titleIdentifier, setTitleIdentifier] = useState(getCompanySettings("preferred_title_identifier"));
    const [filterText, setFilterText] = useState("");
    const [activeCategory, setActiveCategory] = useState<SettingsCategoryId>(SETTINGS_CATEGORIES[0].id);
    const [pressPreferredChannel, setPressPreferredChannel] = useState(getCompanySettings("press_preferred_channel"));

    const [customAccountingColumns, setCustomAccountingColumns] = useState(
        getCompanySettings("accounting_custom_extra_columns"),
    );

    const [consolidationCode, setConsolidationCode] = useState(
        getCompanySettings("payment_file_consolidation_code") ?? "",
    );

    const [gectSettings, setGectSettings] = useState({
        accounting_establishment_code: getCompanySettings("accounting_establishment_code") || "",
        accounting_analytic_service: getCompanySettings("accounting_analytic_service") || "",
        accounting_analytic_operation: getCompanySettings("accounting_analytic_operation") || "",
    });

    const [selectedCurrency, setSelectedCurrency] = useState(companyCurrency);

    const [documents] = useAllPrismicDocumentsByType("changelog");
    const [portal, setPortal] = useState("book");
    const [showChangelog, setShowChangelog] = useState(false);
    const [showSpecificChangelog, setShowSpecificChangelog] = useState(false);
    const [specificChangelog, setSpecificChangelog] = useState();
    const [filterChangelog, setFilterChangelog] = useState("");
    const [showImportant, setShowImportant] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [exportAccountingPeriod, setExportAccountingPeriod] = useState(periods[0]?.id);
    const [availableDistributorsState, setAvailableDistributorsState] = useState(
        availableDistributors?.map((distributor: IDistributor) => distributor.name) ?? [],
    );
    const [authorizedSeatsPerRole, setAuthorizedSeatsPerRole] = useState(
        getCompanySettings("authorized_publisher_seats_per_role"),
    );

    const accountingPnmOptions = [
        { id: "cr", label: "Fin de ligne (CR)", description: "Retour chariot uniquement" },
        { id: "lf", label: "Fin de ligne (LF)", description: "Saut de ligne uniquement" },
        { id: "crlf", label: "Fin de ligne (CRLF)", description: "Retour chariot suivi d'un saut de ligne" },
    ];

    const currentAccountingPnmLinebreak =
        accountingPnmOptions.find((opt) => opt.id === getCompanySettings("accounting_pnm_linebreak")) ??
        accountingPnmOptions[0];

    const activeCurrenciesData = useMemo(() => {
        const currencies: TCurrencyData[] = [];
        activeCurrencies.forEach((currency: string) => {
            const currencyData: TCurrencyData = filteredCurrencies.find((element) => element.code === currency);
            currencies.push(currencyData);
        });
        return currencies;
    }, [activeCurrencies]);

    const [selectedCurrencies, setSelectedCurrencies] = useState<TCurrencyData[]>(activeCurrenciesData ?? []);
    const [currenciesOptions, setCurrenciesOptions] = useState(filteredCurrencies);

    const changelogSortFunc = (
        a: prismic.PrismicDocument<Record<string, any>, string, string>,
        b: prismic.PrismicDocument<Record<string, any>, string, string>,
    ) => prismic.asDate(b.last_publication_date).getTime() - prismic.asDate(a.last_publication_date).getTime();

    const lastImportantChangelog = useMemo(
        () =>
            documents &&
            documents.length > 0 &&
            documents
                .filter((document) => {
                    const portalTypes = document.data.portaltypes.map(
                        (portalType: { portaltype: string }) => portalType.portaltype,
                    );
                    return portalTypes.includes(portal) && document.data.important.length > 0;
                })
                .sort(changelogSortFunc)[0],
        [documents, portal],
    );

    const flattenedChangelog = useMemo(
        () =>
            documents &&
            documents.length > 0 &&
            documents
                .map((doc) => {
                    return {
                        ...doc,
                        title: doc.data.title[0].text,
                        releaseDate: doc.data.releasedate,
                        portaltypes: doc.data.portaltypes
                            .map((portalType: { portaltype: string }) => portalType.portaltype)
                            .join(", "),
                    };
                })
                .filter((changelog: any) => changelog.title.toLowerCase().includes(filterChangelog.toLowerCase()))
                .sort(changelogSortFunc),
        [documents, filterChangelog],
    );

    useEffect(() => {
        if (!channels.length) {
            dispatch(fetchChannels());
        }
        if (!periods.length) {
            dispatch(fetchPeriods());
        }
    }, []);

    useEffect(() => {
        setExportAccountingPeriod(periods[0]?.id);
    }, [periods]);

    const [t] = useTranslation();

    const bulkInvite = () => {
        const confirmation = window.confirm(
            `Are you sure you want to invite every beneficiaries at once? This can take several minutes.`,
        );
        if (confirmation) {
            setIsLoading(true);
            dispatch(bulkInviteToPortal()).then(() => setIsLoading(false));
        }
    };

    const handleOpenChangelog = (portal: string) => {
        setPortal(portal);
        setShowChangelog(true);
    };

    const handleChangelogOpenModal = (row: any) => {
        setShowSpecificChangelog(true);
        setSpecificChangelog(
            (flattenedChangelog as any)[
                (flattenedChangelog as any)?.findIndex((changelog: any) => changelog.id === row)
            ],
        );
    };

    const handleAvailableDistributorChange = (e: any) => {
        setAvailableDistributorsState(e.target.value);
        dispatch(
            putCompanySettings([
                {
                    settingsName: "available_distributors",
                    settingsValue: JSON.stringify(
                        hardcodedDistributorObject
                            .map((item: IDistributor) => (e.target.value.includes(item.name) ? item : null))
                            .filter((item: IDistributor | null) => item !== null),
                    ),
                },
            ]),
        );
    };

    const handleAccountingSoftwareChange = (e: any) => {
        dispatch(
            putCompanySettings([
                {
                    settingsName: "accounting_software",
                    settingsValue: allAccountingSoftwares.find((s) => s.name === e.target.value)?.id || e.target.value,
                },
            ]),
        );
    };

    const handleSaveConsolidationCode = () => {
        dispatch(
            putCompanySettings([
                {
                    settingsName: "payment_file_consolidation_code",
                    settingsValue: consolidationCode.trim(),
                },
            ]),
        );
    };

    const handleSaveGectSettings = () => {
        const normalizedSettings = Object.fromEntries(
            Object.entries(gectSettings).map(([settingsName, settingsValue]) => [
                settingsName,
                settingsValue.trim().toUpperCase(),
            ]),
        ) as typeof gectSettings;
        // The inputs show the saved values
        setGectSettings(normalizedSettings);
        dispatch(
            putCompanySettings(
                Object.entries(normalizedSettings).map(([settingsName, settingsValue]) => ({
                    settingsName,
                    settingsValue,
                })),
            ),
        );
    };

    const handleAccountingPnmLinebreakChange = (e: any) => {
        dispatch(putCompanySettings([{ settingsName: "accounting_pnm_linebreak", settingsValue: e.target.value }]));
    };

    const handleTitleIdentifier = (ev: any) => {
        const value = ev.target.value;
        setTitleIdentifier(value);
        dispatch(putCompanySettings([{ settingsName: "preferred_title_identifier", settingsValue: value }]));
    };

    const handleCurrencyListChange = (value: any) => {
        const parsedValues = value.map((currency) => currency.code);
        setSelectedCurrencies(value);
        dispatch(
            putCompanySettings([
                {
                    settingsName: "active_alternative_currencies",
                    settingsValue: JSON.stringify(parsedValues),
                },
            ]),
        );
    };

    const handlePressPreferredChannel = (e: any) => {
        setPressPreferredChannel(e.target.value);
        dispatch(
            putCompanySettings([
                {
                    settingsName: "press_preferred_channel",
                    settingsValue: e.target.value.toString(),
                },
            ]),
        );
    };

    const handleCurrencyChange = (e: any) => {
        const newCurrency = e.target.value;
        setSelectedCurrency(newCurrency);
        if (companySettings) {
            dispatch(
                patchCompanyInfo({
                    ...companySettings,
                    currency: newCurrency,
                    id: companySettings.id,
                }),
            );
        }
    };

    const changelogColumns: LastElementTableColumns[] = [
        { id: "title", label: "Title", align: "left" },
        { id: "type", label: "Type" },
        { id: "releaseDate", label: "Release date" },
        { id: "portaltypes", label: "Portals" },
    ];

    const renderFlags = (options: any, getTagProps: any) => {
        return options
            .filter((option: any) => option)
            .map((option: any, index: number) => {
                return (
                    <Chip
                        key={index}
                        {...getTagProps({ index })}
                        className="autocomplete-chip"
                        avatar={<img src={option.flag} style={{ border: "1px solid white", borderRadius: "100%" }} />}
                        label={option.code}
                        sx={{
                            background: "light-grey",
                            fontSize: "16px",
                            color: variables["foreground"],
                            marginRight: "0.25rem",
                        }}
                    />
                );
            });
    };

    const renderOptionCurrencies = (props: any, option: any) => {
        const activeCurrenciesCodes = activeCurrenciesData.map((currency) => currency?.code);
        return (
            <li
                {...props}
                key={option.code}
                className={`currencies-autocomplete-options-list ${
                    activeCurrenciesCodes.includes(option.code) ? "selected" : ""
                }`}
            >
                <img src={option.flag} style={{ width: "30px", height: "30px" }} />
                <div className="currencies-info">
                    <span className="currency-name">{option.name}</span>
                    <span className="currency-code">{option.code}</span>
                </div>
            </li>
        );
    };

    const filterCurrenciesOption = (value: string) => {
        const filteredOptions = filteredCurrencies.filter(
            (currency) =>
                currency.name.toLowerCase().includes(value.toLowerCase()) ||
                currency.code.includes(value.toUpperCase()),
        );
        setCurrenciesOptions(filteredOptions);
    };

    const handleAuthorizedPublisherSeatsBlur = (input: string) => {
        const cleanAuthorizedPublisherSeats = parsePositiveIntegerOrInfinity(input);

        if (cleanAuthorizedPublisherSeats === "") {
            toast.error("Authorized publisher seats must be a positive integer or 'Infinity'.");
            return;
        }

        if (cleanAuthorizedPublisherSeats !== authorizedPublisherSeats) {
            dispatch(
                putCompanySettings([
                    {
                        settingsName: "authorized_publisher_seats",
                        settingsValue: cleanAuthorizedPublisherSeats,
                    },
                ]),
            );
        }
    };

    const handleAuthorizedAgreementTemplatesLimitBlur = (input: string) => {
        const cleanAuthorizedAgreementTemplatesLimit = parsePositiveIntegerOrInfinity(input);

        if (cleanAuthorizedAgreementTemplatesLimit === "") {
            toast.error("Authorized agreement templates limit must be a positive integer or 'Infinity'.");
            return;
        }

        if (cleanAuthorizedAgreementTemplatesLimit !== authorizedAgreementTemplatesLimit) {
            dispatch(
                putCompanySettings([
                    {
                        settingsName: "authorized_agreement_templates_limit",
                        settingsValue: cleanAuthorizedAgreementTemplatesLimit,
                    },
                ]),
            );
        }
    };

    const parsePositiveIntegerOrInfinity = (input: string): string => {
        const trimmed = input.trim();
        if (/^\d+$/.test(trimmed)) return trimmed;
        if (/^[Ii]nfinity$/.test(trimmed)) return "Infinity";
        return "";
    };

    const isFilterActive = filterText.trim().length > 0;

    const settingsByCategory = useMemo(() => {
        const needle = filterText.trim().toLowerCase();
        const map = new Map<SettingsCategoryId, typeof BOOLEAN_SETTINGS>();
        for (const setting of BOOLEAN_SETTINGS) {
            if (
                needle &&
                !setting.title.toLowerCase().includes(needle) &&
                !setting.description.toLowerCase().includes(needle)
            )
                continue;
            const bucket = map.get(setting.category) ?? [];
            bucket.push(setting);
            map.set(setting.category, bucket);
        }
        return map;
    }, [filterText]);

    const handleSaveJSONField = async (event, type: "accounting" | "seats") => {
        try {
            event.preventDefault();

            const name =
                type === "accounting" ? "accounting_custom_extra_columns" : "authorized_publisher_seats_per_role";
            const value = type === "accounting" ? customAccountingColumns : authorizedSeatsPerRole;

            JSON.parse(value);

            dispatch(
                putCompanySettings([
                    {
                        settingsName: name,
                        settingsValue: value,
                    },
                ]),
            );
        } catch (error) {
            console.log(error);
            toast.error("Not a valid JSON you are trying to save");
        }
    };

    const cleanedChannels = useFormatChannels(channels, portalType);

    const renderCurrenciesExtra = (checked: boolean) =>
        checked && (
            <Autocomplete
                options={currenciesOptions}
                sx={{ width: "90%", paddingBottom: "16px" }}
                filterOptions={(options) => options}
                value={selectedCurrencies}
                renderOption={renderOptionCurrencies}
                onChange={(e: any, value: any) => handleCurrencyListChange(value)}
                renderInput={(option: any) => (
                    <StyledTextField
                        {...option}
                        placeholder="Select currencies"
                        size="small"
                        onChange={(e) => filterCurrenciesOption(e.target.value)}
                        onBlur={() => setCurrenciesOptions(filteredCurrencies)}
                    />
                )}
                multiple
                renderTags={renderFlags}
                componentsProps={{
                    popper: {
                        className: `autocomplete-form-component`,
                    },
                }}
            />
        );

    const renderAgreementValidationChainExtra = () => (
        <div className="uses-setting company-settings no-border">
            <div style={{ width: "100%" }}>
                <BodyHighlight>Authorized agreement templates limit:</BodyHighlight>
                <div className="settings-field-wrapper">
                    <StyledTextField
                        defaultValue={authorizedAgreementTemplatesLimit ?? "Infinity"}
                        placeholder={authorizedAgreementTemplatesLimit ?? "Infinity"}
                        sx={{ width: "20rem" }}
                        onBlur={(e) => handleAuthorizedAgreementTemplatesLimitBlur(e.target.value)}
                    />
                    <SmallBodyHighlight color="warning">Positive integer or &apos;Infinity&apos;</SmallBodyHighlight>
                </div>
            </div>
        </div>
    );

    const renderMergedStatementsExtra = (checked: boolean) => checked && <ExcludeZeroRoyaltiesStatementsOnMerged />;

    const getBooleanSettingExtra = (settingId: string) => {
        if (settingId === "uses_multiple_currencies") return renderCurrenciesExtra;
        if (settingId === "uses_agreement_validation_chain") return renderAgreementValidationChainExtra;
        if (settingId === "beneficiary_merged_statements_enabled") return renderMergedStatementsExtra;
        return undefined;
    };

    const handleDisableAgreements = () => {
        const docoonIntegration = integrations.find(
            (i) => i.partner === "docoon" && i.status === IntegrationStatus.CONNECTED,
        );
        if (docoonIntegration) dispatch(deleteIntegration(docoonIntegration.id));
    };

    const getBooleanSettingDisableCallback = (settingId: string) => {
        if (settingId === "uses_agreements") return handleDisableAgreements;
        return undefined;
    };

    const renderBooleanSetting = (setting: (typeof BOOLEAN_SETTINGS)[number]) => {
        if (setting.id === "hidden_stock_lines_on_statement") {
            return (
                <Fragment key={setting.id}>
                    <div className="company-settings">
                        <div>
                            <BodyHighlight>{filterTextContent(setting.title, filterText)}</BodyHighlight>
                            <SmallBody color="grey">{filterTextContent(setting.description, filterText)}</SmallBody>
                        </div>
                    </div>
                    <HiddenStockLinesOnStatement />
                </Fragment>
            );
        }

        return (
            <BooleanSettingRow
                key={setting.id}
                setting={setting}
                filterText={filterText}
                renderExtra={getBooleanSettingExtra(setting.id)}
                onDisable={getBooleanSettingDisableCallback(setting.id)}
            />
        );
    };

    /**
     * Non-boolean controls grouped per category. Hidden while a filter is active
     * so filtering stays focused on toggles only.
     */
    const extrasByCategory: Partial<Record<SettingsCategoryId, ReactNode>> = {
        [SETTINGS_CATEGORY.Beneficiaries]: (
            <>
                {usesPortal && (
                    <div className="uses-portal-setting company-settings">
                        <div>
                            <BodyHighlight>Invite all beneficiaries to the portal at once</BodyHighlight>
                            <SmallBody color="grey">Aka Inviter tous les bénéficiaires à l{"'"}Espace auteur</SmallBody>
                        </div>
                        {isLoading ? (
                            <Spinner />
                        ) : (
                            <StyledButton buttonType="outlined" onClick={bulkInvite}>
                                <SendOutlinedIcon sx={{ height: "14px", width: "14px" }} />
                                Lancer l{"'"}invitation
                            </StyledButton>
                        )}
                    </div>
                )}
                <div className="uses-setting company-settings no-border">
                    <div style={{ width: "100%" }}>
                        <BodyHighlight>Nombre de places autorisées:</BodyHighlight>
                        <div className="settings-field-wrapper">
                            <StyledTextField
                                defaultValue={authorizedPublisherSeats}
                                placeholder={authorizedPublisherSeats}
                                sx={{ width: "20rem" }}
                                onBlur={(e) => handleAuthorizedPublisherSeatsBlur(e.target.value)}
                            />
                            <SmallBodyHighlight color="warning">
                                Entier positif ou &apos;Infinity&apos;
                            </SmallBodyHighlight>
                        </div>
                    </div>
                </div>
                <div className="settings-field-wrapper">
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <BodyHighlight>Authorised seats per role</BodyHighlight>{" "}
                        <InfoTooltip
                            text={'To complete only with necessary roles as such: {owner: 6, "view-only": 2}'}
                            link="https://www.notion.so/crealo/Les-r-les-utilisateurs-1e54113b1ae64f6f92cdf4f17ba7cb9c#da11fff743a04dedb895417c806338ef"
                        />
                    </div>
                    <StyledTextField
                        name="seatsPerRole"
                        maxRows={24}
                        style={{ width: 640 }}
                        value={authorizedSeatsPerRole}
                        onChange={(event) => setAuthorizedSeatsPerRole(event.target.value)}
                        multiline
                    />
                    <StyledButton buttonType="primary" type={"submit"} onClick={(e) => handleSaveJSONField(e, "seats")}>
                        {t("NewElement.Validate")}
                    </StyledButton>
                </div>
            </>
        ),
        [SETTINGS_CATEGORY.Titles]: (
            <>
                <div className="settings-field-wrapper">
                    <BodyHighlight>Company Currency :</BodyHighlight>
                    <StyledSelect
                        value={selectedCurrency}
                        onChange={handleCurrencyChange}
                        sx={{ width: "20rem" }}
                        size="small"
                    >
                        {currenciesList().map((currency) => (
                            <StyledMenuItem key={currency.code} value={currency.code}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <img src={currency.flag} style={{ width: "20px", height: "20px" }} />
                                    {currency.code} - {currency.name}
                                </div>
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </div>
                {["audiovisual", "press"].includes(portalType) && (
                    <>
                        <div className="company-settings no-border">
                            <div>
                                <BodyHighlight>{t("CompanySettings.TitleIdentifierHead")}</BodyHighlight>
                                <StyledSelect
                                    value={titleIdentifier}
                                    onChange={(ev) => handleTitleIdentifier(ev)}
                                    size="small"
                                    sx={{ width: "20rem", paddingLeft: "1.25rem" }}
                                >
                                    <StyledMenuItem value="concat">
                                        {t("CompanySettings.TitleIdentifier.Concat")}
                                    </StyledMenuItem>
                                    <StyledMenuItem value="unique">
                                        {t("CompanySettings.TitleIdentifier.Unique")}
                                    </StyledMenuItem>
                                </StyledSelect>
                            </div>
                        </div>
                        <div className="company-settings no-border">
                            <div>
                                <BodyHighlight>{t("CompanySettings.PreferredChannel")}</BodyHighlight>
                                <StyledSelect
                                    size="small"
                                    sx={{ width: "20rem", paddingLeft: "1.25rem" }}
                                    value={pressPreferredChannel}
                                    onChange={(ev) => handlePressPreferredChannel(ev)}
                                >
                                    {cleanedChannels.map((channel) => {
                                        if (!usesMinimumGuaranteed && channel.name === t("NewChannel.MinimumGuarantee"))
                                            return;
                                        return (
                                            <StyledMenuItem key={`channel-${channel.id}`} value={channel.id}>
                                                {channel.name}
                                            </StyledMenuItem>
                                        );
                                    })}
                                </StyledSelect>
                            </div>
                        </div>
                    </>
                )}
            </>
        ),
        [SETTINGS_CATEGORY.Sales]: (
            <div className="settings-field-wrapper">
                <BodyHighlight>Available distributors :</BodyHighlight>
                <StyledSelect
                    value={availableDistributorsState}
                    onChange={handleAvailableDistributorChange}
                    multiple
                    sx={{ width: "20rem" }}
                    size="small"
                >
                    {hardcodedDistributorList.map((distributor) => (
                        <StyledMenuItem key={`distributor-${distributor.label}`} value={distributor.value}>
                            {distributor.label}
                        </StyledMenuItem>
                    ))}
                </StyledSelect>
            </div>
        ),
        [SETTINGS_CATEGORY.Statements]: (
            <>
                <div className="company-settings no-border">
                    <div>
                        <BodyHighlight>{t("AdminPanel.PeriodTypes")}</BodyHighlight>
                        <SmallBody color="grey">{t("AdminPanel.PeriodTypesDescription")}</SmallBody>
                    </div>
                </div>
                <AddPeriodType />
            </>
        ),
        [SETTINGS_CATEGORY.Payments]: (
            <>
                <div className="settings-field-wrapper">
                    <BodyHighlight>Accounting software :</BodyHighlight>
                    <StyledSelect
                        value={currentAccountingSoftware}
                        onChange={handleAccountingSoftwareChange}
                        sx={{ width: "20rem" }}
                        size="small"
                    >
                        {allAccountingSoftwares.map(({ id, name }) => (
                            <StyledMenuItem key={id} value={id}>
                                {name || id}
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </div>
                {currentAccountingSoftware === "cegid_gect" && (
                    <div className="settings-field-wrapper">
                        <BodyHighlight>
                            Cegid GECT (establishment / analytic service / analytic operation) :
                        </BodyHighlight>
                        {(
                            [
                                ["accounting_establishment_code", "EDMIL", 6],
                                ["accounting_analytic_service", "EDIT", 6],
                                ["accounting_analytic_operation", "Z", 10],
                            ] as const
                        ).map(([name, placeholder, maxLength]) => (
                            <StyledTextField
                                key={name}
                                name={name}
                                style={{ width: "10rem" }}
                                size="small"
                                value={gectSettings[name]}
                                onChange={(e) => setGectSettings({ ...gectSettings, [name]: e.target.value })}
                                inputProps={{ maxLength }}
                                placeholder={placeholder}
                            />
                        ))}
                        <StyledButton buttonType="primary" onClick={handleSaveGectSettings}>
                            {t("NewElement.Validate")}
                        </StyledButton>
                    </div>
                )}
                {currentAccountingSoftware === "arte" && (
                    <div className="settings-field-wrapper">
                        <BodyHighlight>Custom column to attach on accounting export (XLSX)</BodyHighlight>
                        <StyledTextField
                            name="customAccountingColumns"
                            maxRows={24}
                            style={{ width: 640 }}
                            value={customAccountingColumns}
                            onChange={(event) => setCustomAccountingColumns(event.target.value)}
                            multiline
                        />
                        <StyledButton
                            buttonType="primary"
                            type={"submit"}
                            onClick={(e) => handleSaveJSONField(e, "accounting")}
                        >
                            {t("NewElement.Validate")}
                        </StyledButton>
                    </div>
                )}
                <div className="settings-field-wrapper">
                    <BodyHighlight>Payment file consolidation code :</BodyHighlight>
                    <StyledTextField
                        name="consolidationCode"
                        style={{ width: "20rem" }}
                        size="small"
                        value={consolidationCode}
                        onChange={(e) => setConsolidationCode(e.target.value)}
                        inputProps={{ maxLength: 30 }}
                        placeholder="7P10"
                    />
                    <StyledButton
                        buttonType="primary"
                        onClick={handleSaveConsolidationCode}
                        disabled={!consolidationCode.trim()}
                    >
                        {t("NewElement.Validate")}
                    </StyledButton>
                </div>
                <div className="settings-field-wrapper">
                    <BodyHighlight>Accounting PNM file - Line Break Type</BodyHighlight>
                    <StyledSelect
                        value={currentAccountingPnmLinebreak.id}
                        sx={{ width: "20rem" }}
                        size="small"
                        onChange={handleAccountingPnmLinebreakChange}
                        renderValue={(value) => {
                            const label = accountingPnmOptions.find((opt) => opt.id === value)?.label;
                            return <div>{label}</div>;
                        }}
                    >
                        {accountingPnmOptions.map(({ id, label, description }) => (
                            <StyledMenuItem key={id} value={id}>
                                <div>
                                    {label}
                                    <SmallBody color="grey">{description}</SmallBody>
                                </div>
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </div>
                <div className="uses-setting company-settings no-border">
                    <div>
                        <BodyHighlight>Télécharger la compta des contrats reportées pour la période:</BodyHighlight>
                        <select
                            onChange={(e) => setExportAccountingPeriod(+e.target.value)}
                            defaultValue={periods[0]?.id}
                        >
                            {periods.map((period, i) => (
                                <option key={i} value={period.id}>
                                    {formatLocaleDateString({
                                        date: period.periodStart,
                                        options: { numeric: true, timeZone: "UTC" },
                                    })}{" "}
                                    -{" "}
                                    {formatLocaleDateString({
                                        date: period.periodEnd,
                                        options: { numeric: true, timeZone: "UTC" },
                                    })}
                                </option>
                            ))}
                        </select>
                    </div>
                    <StyledButton
                        buttonType="outlined"
                        onClick={() => {
                            dispatch(fetchExportUnpaidAccounting({ periodId: exportAccountingPeriod }));
                        }}
                    >
                        <FileDownloadOutlinedIcon sx={{ height: "18px", width: "18px" }} />
                        Télécharger
                    </StyledButton>
                </div>
            </>
        ),
        [SETTINGS_CATEGORY.Enterprise]: (
            <>
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    <StyledButton buttonType="primary" onClick={() => handleOpenChangelog("book")}>
                        Open last book changelog
                    </StyledButton>
                    <StyledButton buttonType="secondary" onClick={() => handleOpenChangelog("press")}>
                        Open last press changelog
                    </StyledButton>
                    <StyledButton buttonType="outlined" onClick={() => handleOpenChangelog("beneficiary")}>
                        Open last beneficiary changelog
                    </StyledButton>
                </div>
                <br />
                <StyledTextField
                    fullWidth
                    placeholder="Search changelog by title"
                    value={filterChangelog}
                    onChange={(e: any) => setFilterChangelog(e.target.value)}
                />
                <StyledButton
                    buttonType={showImportant ? "primary" : "secondary"}
                    onClick={() => setShowImportant((prev) => !prev)}
                    sx={{ marginTop: "16px" }}
                >
                    {showImportant ? "Show important modal content" : "Show changelog content"}
                </StyledButton>
                <LastElementTable
                    columns={changelogColumns}
                    data={flattenedChangelog}
                    max={-1}
                    onRowClick={handleChangelogOpenModal}
                    stickyFooter
                />
            </>
        ),
    };

    const visibleCategories = SETTINGS_CATEGORIES.filter((category) => {
        const hasBooleans = (settingsByCategory.get(category.id)?.length ?? 0) > 0;
        const hasExtras = !isFilterActive && Boolean(extrasByCategory[category.id]);
        return hasBooleans || hasExtras;
    });

    /**
     * When the filter removes the active category, fall back to the first match.
     * Prevents the content pane from going blank while the matching tab is sitting
     * right there, selectable.
     */
    useEffect(() => {
        if (!visibleCategories.length) return;
        if (!visibleCategories.some((category) => category.id === activeCategory)) {
            setActiveCategory(visibleCategories[0].id);
        }
    }, [visibleCategories, activeCategory]);

    const activeCategoryData = visibleCategories.find((category) => category.id === activeCategory);
    const activeBooleans = activeCategoryData ? settingsByCategory.get(activeCategoryData.id) ?? [] : [];
    const activeExtras = activeCategoryData && !isFilterActive ? extrasByCategory[activeCategoryData.id] : undefined;

    return (
        <div className="CompanyHiddenSettings" style={{ paddingBlock: "20px" }}>
            <StyledTextField
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Filter by title or description"
                fullWidth
                sx={{ marginBottom: "16px" }}
            />

            <StyledTabs
                value={activeCategoryData?.id ?? false}
                onChange={(_, value) => setActiveCategory(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    minHeight: "36px",
                    "& .MuiTab-root": {
                        fontSize: "12.5px !important",
                        minHeight: "36px",
                        padding: "6px 12px",
                        marginRight: "16px !important",
                    },
                    "& .MuiTab-root:first-of-type": {
                        paddingLeft: 0,
                    },
                    "& .Mui-selected": {
                        fontSize: "12.5px !important",
                    },
                }}
            >
                {visibleCategories.map((category) => {
                    const count = settingsByCategory.get(category.id)?.length ?? 0;
                    return (
                        <Tab
                            key={category.id}
                            value={category.id}
                            label={count > 0 ? `${category.title} (${count})` : category.title}
                        />
                    );
                })}
            </StyledTabs>

            <div style={{ marginTop: "24px" }}>
                {activeCategoryData?.description && (
                    <SmallBody color="grey" style={{ display: "block", marginBottom: "8px" }}>
                        {activeCategoryData.description}
                    </SmallBody>
                )}
                {activeBooleans.map(renderBooleanSetting)}
                {activeExtras}
            </div>

            <ChangelogModal
                isOpen={showChangelog}
                changelogMessage={lastImportantChangelog}
                onClose={() => setShowChangelog(false)}
            />
            <ChangelogModal
                isOpen={showSpecificChangelog}
                changelogMessage={specificChangelog}
                onClose={() => setShowSpecificChangelog(false)}
                isImportant={showImportant}
            />
        </div>
    );
};

export default CompanyHiddenSettings;
